import { api } from "@spark/web";
import * as z from "zod";
import {
	CreateEmbedCodeDtoSchema,
	DocumentProgressSchema,
	EmbedCodeSchema,
	StoredDocumentProgressSchema,
	UpdateDocumentProgressDtoSchema,
	UserSettingsPatchSchema,
	UserSettingsSchema,
} from "./domain/index.schemas.ts";

/*
 * The reader is opened with a short URL and works without logging in, so none
 * of these routes require a session. Progress and settings are personal: with a
 * session they are read and written, without one they degrade to "nothing
 * stored" (200) and "nothing to store" (204).
 */

const DocumentPathSchema = z.object({ documentId: z.string() });

const CreateEmbedCode = api
	.route({ method: "POST", path: "/embed-code" })
	.meta({
		description:
			"Exchange the code from a short URL for a Readiant embed of that document.",
	})
	.input("body", CreateEmbedCodeDtoSchema)
	.output(201, EmbedCodeSchema);

const GetDocumentProgress = api
	.route({ method: "GET", path: "/documents/{documentId}/progress" })
	.meta({
		description:
			"The page the reader was left on. Responds with `null` without a session.",
	})
	.input("path", DocumentPathSchema)
	.output(200, DocumentProgressSchema);

const UpdateDocumentProgress = api
	.route({ method: "PUT", path: "/documents/{documentId}/progress" })
	.meta({
		description:
			"Store the page the reader navigated to. Without a session nothing is stored (204).",
	})
	.input("path", DocumentPathSchema)
	.input("body", UpdateDocumentProgressDtoSchema)
	.output(200, StoredDocumentProgressSchema)
	.output(204, z.undefined());

const GetUserSettings = api
	.route({ method: "GET", path: "/user-settings" })
	.meta({
		description:
			"Reading preferences of the user. Responds with `null` without a session.",
	})
	.output(
		200,
		z.object({ userSettings: UserSettingsSchema.nullable() }).meta({
			examples: [
				{ userSettings: { font: "Lora", fontSize: 6 } },
				{ userSettings: null },
			],
		}),
	);

const UpdateUserSettings = api
	.route({ method: "PATCH", path: "/user-settings" })
	.meta({
		description:
			"Change reading preferences. Without a session nothing is stored (204).",
	})
	.input("body", z.object({ userSettings: UserSettingsPatchSchema }))
	.output(
		200,
		z.object({ userSettings: UserSettingsSchema }).meta({
			examples: [{ userSettings: { font: "Lora", fontSize: 6 } }],
		}),
	)
	.output(204, z.undefined());

/**
 * Routes of the reader backend-for-frontend, exported separately so book-api can
 * document them alongside its public API.
 */
export const BffRouter = api
	.router()
	.meta({ description: "Backend-for-frontend of the reader." })
	.add("createEmbedCode", CreateEmbedCode)
	.add("getDocumentProgress", GetDocumentProgress)
	.add("updateDocumentProgress", UpdateDocumentProgress)
	.add("getUserSettings", GetUserSettings)
	.add("updateUserSettings", UpdateUserSettings)
	.prefix("/_api");

/**
 * Contract of the reader backend-for-frontend served by book-api.
 */
export const BffContract = api
	.contract({ title: "Reader BFF" })
	.add("bff", BffRouter);
